import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  User,
  Mail,
  Lock,
  Bell,
  Shield,
  Link2,
  CheckCircle,
  ExternalLink,
  LogOut,
  Camera,
  Save,
  Briefcase,
  Target,
  FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Microsoft icon SVG
const MicrosoftIcon = () => (
  <svg viewBox="0 0 21 21" className="w-5 h-5">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

// Google icon SVG
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const Settings = () => {
  const { user, getAuthHeaders, logout, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState({ microsoft: null, google: null });
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    title: '',
    company: '',
    avatar_url: '',
    about_me: { expertise: '', business_plan: '', focus_areas: '' }
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [notifications, setNotifications] = useState({
    email_new_lead: true,
    email_commission: true,
    email_follow_up: true,
    push_enabled: false
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchProfile();
    fetchConnectedAccounts();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/profile`, getAuthHeaders());
      setProfile(response.data);
      if (response.data.notifications) {
        setNotifications(response.data.notifications);
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  const fetchConnectedAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/connected-accounts`, getAuthHeaders());
      setConnectedAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch connected accounts:', error);
    }
  };

  const handleProfileSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/users/profile`, profile, getAuthHeaders());
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationsSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/users/notifications`, notifications, getAuthHeaders());
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, getAuthHeaders());
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectMicrosoft = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback/microsoft';
    window.location.href = `${API_URL}/api/auth/microsoft/connect?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleConnectGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback/google';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleDisconnectAccount = async (provider) => {
    try {
      await axios.delete(`${API_URL}/api/auth/disconnect/${provider}`, getAuthHeaders());
      toast.success(`${provider} account disconnected`);
      fetchConnectedAccounts();
    } catch (error) {
      toast.error(`Failed to disconnect ${provider}`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-8"
      data-testid="settings-page"
    >
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-[#1B365D] tracking-tight">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account settings and preferences</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-full">
          <TabsTrigger value="profile" className="rounded-full data-[state=active]:bg-white">
            <User className="w-4 h-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="connections" className="rounded-full data-[state=active]:bg-white">
            <Link2 className="w-4 h-4 mr-2" />
            Connections
          </TabsTrigger>
          <TabsTrigger value="notifications" className="rounded-full data-[state=active]:bg-white">
            <Bell className="w-4 h-4 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="security" className="rounded-full data-[state=active]:bg-white">
            <Shield className="w-4 h-4 mr-2" />
            Security
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="border-0 shadow-apple">
            <CardHeader>
              <CardTitle className="text-lg text-[#1B365D]">Profile Information</CardTitle>
              <CardDescription>Update your personal information and profile picture</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Avatar Section */}
              <div className="flex items-center gap-6">
                <Avatar className="w-24 h-24">
                  <AvatarImage src={photoPreview || profile.avatar_url} />
                  <AvatarFallback className="bg-[#1B365D] text-white text-2xl">
                    {profile.name?.charAt(0)?.toUpperCase() || user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              toast.error('File must be under 5MB');
                              return;
                            }
                            setPhotoFile(file);
                            setPhotoPreview(URL.createObjectURL(file));
                          }
                        }}
                        data-testid="photo-upload-input"
                      />
                      <span className="inline-flex items-center gap-2 px-4 py-2 border rounded-full text-sm font-medium hover:bg-slate-50 cursor-pointer transition-colors">
                        <Camera className="w-4 h-4" />
                        {profile.avatar_url ? 'Change Photo' : 'Add Photo'}
                      </span>
                    </label>
                    {(profile.avatar_url || photoPreview) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600"
                        onClick={async () => {
                          try {
                            await axios.delete(`${API_URL}/api/users/profile/photo`, getAuthHeaders());
                            setProfile({ ...profile, avatar_url: '' });
                            setPhotoPreview(null);
                            setPhotoFile(null);
                            toast.success('Photo removed');
                          } catch (e) {
                            toast.error('Failed to remove photo');
                          }
                        }}
                        data-testid="remove-photo-btn"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  {photoFile && (
                    <Button
                      size="sm"
                      className="bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full"
                      disabled={uploadingPhoto}
                      onClick={async () => {
                        setUploadingPhoto(true);
                        try {
                          const formData = new FormData();
                          formData.append('file', photoFile);
                          const resp = await axios.post(`${API_URL}/api/users/profile/photo`, formData, {
                            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                          });
                          setProfile({ ...profile, avatar_url: resp.data.avatar_url });
                          setPhotoFile(null);
                          setPhotoPreview(null);
                          toast.success('Photo uploaded!');
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Failed to upload photo');
                        } finally {
                          setUploadingPhoto(false);
                        }
                      }}
                      data-testid="save-photo-btn"
                    >
                      {uploadingPhoto ? 'Uploading...' : 'Save Photo'}
                    </Button>
                  )}
                  <p className="text-xs text-slate-500">JPG, PNG. Max 5MB</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    placeholder="John Doe"
                    data-testid="profile-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    placeholder="john@example.com"
                    data-testid="profile-email"
                  />
                  <p className="text-xs text-slate-500">Update your login email address</p>
                </div>
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input
                    value={profile.phone || ''}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                    data-testid="profile-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Job Title</Label>
                  <Input
                    value={profile.title || ''}
                    onChange={(e) => setProfile({ ...profile, title: e.target.value })}
                    placeholder="Sales Representative"
                    data-testid="profile-title"
                  />
                </div>
              </div>

              {/* About Me Section */}
              <div className="border-t border-slate-100 pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Briefcase className="w-5 h-5 text-[#1B365D]" />
                  <h3 className="font-semibold text-[#1B365D]">About Me</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">Help your admin team understand your strengths and strategy. This information is visible to administrators.</p>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <Target className="w-3.5 h-3.5 text-slate-400" />
                      Sales Expertise
                    </Label>
                    <textarea
                      value={profile.about_me?.expertise || ''}
                      onChange={(e) => setProfile({ ...profile, about_me: { ...profile.about_me, expertise: e.target.value } })}
                      placeholder="e.g., Hospital systems, dental practices, specialty clinics, home health agencies..."
                      rows={3}
                      className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0071E3] focus:border-transparent resize-none"
                      data-testid="about-expertise"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-slate-400" />
                      Business Plan
                    </Label>
                    <textarea
                      value={profile.about_me?.business_plan || ''}
                      onChange={(e) => setProfile({ ...profile, about_me: { ...profile.about_me, business_plan: e.target.value } })}
                      placeholder="e.g., My strategy involves leveraging existing network of healthcare administrators in the tri-state area..."
                      rows={3}
                      className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0071E3] focus:border-transparent resize-none"
                      data-testid="about-business-plan"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <Briefcase className="w-3.5 h-3.5 text-slate-400" />
                      Primary Focus Areas for Lead Gen
                    </Label>
                    <textarea
                      value={profile.about_me?.focus_areas || ''}
                      onChange={(e) => setProfile({ ...profile, about_me: { ...profile.about_me, focus_areas: e.target.value } })}
                      placeholder="e.g., Northeast region, private practices with 5-50 employees, urgent care centers..."
                      rows={3}
                      className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0071E3] focus:border-transparent resize-none"
                      data-testid="about-focus-areas"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={handleProfileSave} 
                  disabled={loading}
                  className="bg-[#1B365D] hover:bg-[#1B365D]/90"
                  data-testid="save-profile-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Connections Tab */}
        <TabsContent value="connections">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Microsoft Connection */}
            <Card className="border-0 shadow-apple">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                      <MicrosoftIcon />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Microsoft 365</h3>
                      <p className="text-sm text-slate-500">Connect for email & calendar sync</p>
                    </div>
                  </div>
                </div>
                
                {connectedAccounts.microsoft ? (
                  <div className="mt-4 p-4 bg-emerald-50 rounded-xl">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium">Connected</span>
                    </div>
                    <p className="text-sm text-emerald-600 mt-1">{connectedAccounts.microsoft.email}</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3 text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleDisconnectAccount('microsoft')}
                      data-testid="disconnect-microsoft"
                    >
                      Disconnect
                    </Button>
                  </div>
                ) : (
                  <Button 
                    onClick={handleConnectMicrosoft}
                    className="mt-4 w-full bg-[#0078D4] hover:bg-[#0078D4]/90"
                    data-testid="connect-microsoft"
                  >
                    <MicrosoftIcon />
                    <span className="ml-2">Connect Microsoft Account</span>
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Google Connection */}
            <Card className="border-0 shadow-apple">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                      <GoogleIcon />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Google Workspace</h3>
                      <p className="text-sm text-slate-500">Connect for Gmail & Google Calendar</p>
                    </div>
                  </div>
                </div>
                
                {connectedAccounts.google ? (
                  <div className="mt-4 p-4 bg-emerald-50 rounded-xl">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium">Connected</span>
                    </div>
                    <p className="text-sm text-emerald-600 mt-1">{connectedAccounts.google.email}</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3 text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleDisconnectAccount('google')}
                      data-testid="disconnect-google"
                    >
                      Disconnect
                    </Button>
                  </div>
                ) : (
                  <Button 
                    onClick={handleConnectGoogle}
                    className="mt-4 w-full bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid="connect-google"
                  >
                    <GoogleIcon />
                    <span className="ml-2">Connect Google Account</span>
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="border-0 shadow-apple mt-6">
            <CardHeader>
              <CardTitle className="text-lg text-[#1B365D]">What You Can Do With Connected Accounts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <Mail className="w-6 h-6 text-[#1B365D] mb-2" />
                  <h4 className="font-medium text-slate-900">Email Integration</h4>
                  <p className="text-sm text-slate-500 mt-1">Send and receive emails directly from the CRM</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl">
                  <Bell className="w-6 h-6 text-[#1B365D] mb-2" />
                  <h4 className="font-medium text-slate-900">Calendar Sync</h4>
                  <p className="text-sm text-slate-500 mt-1">Schedule meetings and demos with leads</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl">
                  <Link2 className="w-6 h-6 text-[#1B365D] mb-2" />
                  <h4 className="font-medium text-slate-900">Single Sign-On</h4>
                  <p className="text-sm text-slate-500 mt-1">Quick login with your work account</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card className="border-0 shadow-apple">
            <CardHeader>
              <CardTitle className="text-lg text-[#1B365D]">Notification Preferences</CardTitle>
              <CardDescription>Choose how you want to be notified about activity</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <h4 className="font-medium text-slate-900">New Lead Notifications</h4>
                    <p className="text-sm text-slate-500">Get notified when a new lead is assigned to you</p>
                  </div>
                  <Switch 
                    checked={notifications.email_new_lead}
                    onCheckedChange={(checked) => setNotifications({ ...notifications, email_new_lead: checked })}
                    data-testid="notify-new-lead"
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <h4 className="font-medium text-slate-900">Commission Updates</h4>
                    <p className="text-sm text-slate-500">Receive alerts about commission earnings and payouts</p>
                  </div>
                  <Switch 
                    checked={notifications.email_commission}
                    onCheckedChange={(checked) => setNotifications({ ...notifications, email_commission: checked })}
                    data-testid="notify-commission"
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <h4 className="font-medium text-slate-900">Follow-up Reminders</h4>
                    <p className="text-sm text-slate-500">Get reminded about pending follow-ups</p>
                  </div>
                  <Switch 
                    checked={notifications.email_follow_up}
                    onCheckedChange={(checked) => setNotifications({ ...notifications, email_follow_up: checked })}
                    data-testid="notify-followup"
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <h4 className="font-medium text-slate-900">Push Notifications</h4>
                    <p className="text-sm text-slate-500">Enable browser push notifications</p>
                  </div>
                  <Switch 
                    checked={notifications.push_enabled}
                    onCheckedChange={(checked) => setNotifications({ ...notifications, push_enabled: checked })}
                    data-testid="notify-push"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={handleNotificationsSave} 
                  disabled={loading}
                  className="bg-[#1B365D] hover:bg-[#1B365D]/90"
                  data-testid="save-notifications-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Preferences
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card className="border-0 shadow-apple">
            <CardHeader>
              <CardTitle className="text-lg text-[#1B365D]">Change Password</CardTitle>
              <CardDescription>Update your password to keep your account secure</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label>Current Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    placeholder="••••••••"
                    data-testid="current-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    placeholder="••••••••"
                    data-testid="new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    placeholder="••••••••"
                    data-testid="confirm-password"
                  />
                </div>
              </div>

              <Button 
                onClick={handlePasswordChange}
                disabled={loading || !passwordForm.current_password || !passwordForm.new_password}
                className="bg-[#1B365D] hover:bg-[#1B365D]/90"
                data-testid="change-password-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Update Password
              </Button>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-apple mt-6">
            <CardHeader>
              <CardTitle className="text-lg text-red-600">Danger Zone</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 border border-red-200 rounded-xl bg-red-50">
                <div>
                  <h4 className="font-medium text-red-900">Sign Out of All Devices</h4>
                  <p className="text-sm text-red-600">This will log you out from all active sessions</p>
                </div>
                <Button 
                  variant="outline" 
                  className="text-red-600 border-red-300 hover:bg-red-100"
                  onClick={logout}
                  data-testid="logout-all"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out All
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
};

export default Settings;
